import React, { useState, useRef, useLayoutEffect } from 'react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';

const isDefaultSort = (value, defaultSort) =>
	Boolean(
		value &&
		defaultSort &&
		value.value === defaultSort.column &&
		value.asc === defaultSort.asc
	);

const SortInput = ({
	placeholder,
	options,
	handleSort,
	onResetSort,
	sort,
	defaultSort,
	iconOnly = false
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [localValue, setLocalValue] = useState(null);
	const [menuWidth, setMenuWidth] = useState(undefined);
	const dropdownRef = useRef(null);
	const isControlled = sort !== undefined;

	const value = (() => {
		if (!isControlled) return localValue;
		if (!sort?.column) return null;
		const option = options?.find(o => o.value === sort.column);
		if (!option) return null;
		return { ...option, asc: sort.asc };
	})();

	// Floating UI setup
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(2), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const handleOptionClick = option => {
		const isSame = value?.value === option.value;

		if (
			isSame &&
			value?.asc === false &&
			value?.value !== defaultSort?.column
		) {
			if (!isControlled) {
				setLocalValue(null);
			}
			if (onResetSort) {
				onResetSort();
			} else {
				handleSort(null);
			}
			return;
		}

		const asc = isSame ? !value?.asc : true;
		if (!isControlled) {
			setLocalValue({ ...option, asc });
		}
		handleSort(option.value, asc);
	};

	useLayoutEffect(() => {
		if (isOpen && dropdownRef.current) {
			setMenuWidth(dropdownRef.current.offsetWidth);
		}
	}, [isOpen]);

	return (
		<div
			className={`sort-input dropdown-ats ${iconOnly ? 'is-icon-only' : ''}`}
			ref={dropdownRef}>
			<button
				ref={refs.setReference}
				className={`dropdown-ats-btn ${iconOnly ? 'is-icon-only' : ''} ${value?.className || ''}`}
				type="button"
				{...getReferenceProps()}>
				<img src="/ats/icons/sort-icon.svg" alt="" />
				{iconOnly ? null : value?.label || placeholder || 'Sort by'}
				{value && !isDefaultSort(value, defaultSort) ? (
					<span className="sort-applied-indicator" aria-hidden="true" />
				) : null}
			</button>
			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<ul
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								minWidth: menuWidth
							}}
							{...getFloatingProps()}
							className="dropdown-ats-menu sort-input">
							{options?.map(option => {
								const isSelected = value?.value === option.value;
								const showArrow = isSelected;

								return (
									<li
										key={option.value}
										className={`${option.className || ''} ${
											isSelected ? 'is-selected' : ''
										}`}
										onClick={() => handleOptionClick(option)}>
										{option.label}
										{showArrow ? (
											<button
												className="arrow-btn"
												type="button"
												onClick={e => {
													e.stopPropagation();
													handleOptionClick(option);
												}}>
												<img
													src={`/ats/icons/arrow-${
														isSelected && value?.asc ? 'up' : 'down'
													}-icon.svg`}
													alt=""
												/>
											</button>
										) : null}
									</li>
								);
							})}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
};

export default SortInput;
